import { Box } from '@chakra-ui/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Error } from '@/components/';
import { nullError } from '@/components/error.js';

import APILoadingView from '../../plans/edit/csv/APILoadingView.jsx';
import APIResultsView from '../../plans/edit/csv/APIResultsView.jsx';
import CSVTemplateView from '../../plans/edit/csv/CSVTemplateView.jsx';
import VerifyCSVTableView from '../../plans/edit/csv/VerifyCSVTableView.jsx';
import VerifyCSVView from '../../plans/edit/csv/VerifyCSVView.jsx';

import useCSVInternationalRates from './useCSVInternationalRates.js';
import useCSVTemplate from './useCSVTemplate.js';
import useBulkUpdate from './useBulkUpdate.js';
import {
    failedAPIColumns,
    failedColumns,
    passedColumns,
    succeedAPIColumns,
} from './constants.jsx';

const DOWNLOAD_CSV_TEMPLATE = 'DOWNLOAD_CSV_TEMPLATE';
const LOADING_CSV_VALIDATION = 'LOADING_CSV_VALIDATION';
const CSV_VALIDATION_RESULTS = 'CSV_VALIDATION_RESULTS';
const API_CALLS_PROGRESS = 'API_CALLS_PROGRESS';
const API_CALLS_RESULTS = 'API_CALLS_RESULTS';
const CSV_DATA_NOT_FOUND = 'CSV_DATA_NOT_FOUND';

const useCSVDisplayControl = () => {
    const [viewVerificationResults, setViewVerificationResults] =
        useState(false);
    const [viewAPIResults, setViewAPIResults] = useState(false);

    const {
        handleUploadData,
        uploadData,
        verificationChecks,
        loading: verificationLoading,
        error: verificationErrors,
        payloads,
        verificationResult,
        handleReset: handleResetCSV,
        handleExportInvalidData,
    } = useCSVInternationalRates();

    const {
        handlePayloadChange,
        loading: apiLoading,
        counter: completionCounter,
        errorCounter,
        error: apiErrors,
        succeed: apiSucceed,
        handleExportFailedData,
        handleReset: handleResetBulkUpdate,
        total,
    } = useBulkUpdate();

    const handleViewVerificationResults = () =>
        setViewVerificationResults(true);

    const handleViewAPIResults = () => setViewAPIResults(true);

    const handleStartOver = () => {
        setViewVerificationResults(false);
        setViewAPIResults(false);
        handleResetCSV();
        handleResetBulkUpdate();
    };

    const handleCurrentDisplay = () => {
        if (verificationErrors) return CSV_DATA_NOT_FOUND;
        if (!uploadData) return DOWNLOAD_CSV_TEMPLATE;
        if (viewAPIResults) return API_CALLS_RESULTS;
        if (apiLoading) return API_CALLS_PROGRESS;
        if (viewVerificationResults) return CSV_VALIDATION_RESULTS;
        if (verificationLoading) return LOADING_CSV_VALIDATION;
    };

    const handleCancelValidationTable = () => {
        handleResetCSV();
        setViewVerificationResults(false);
    };

    const handleStartAPICall = () => {
        handlePayloadChange(payloads);
        setViewVerificationResults(false);
    };

    return {
        handleUploadData,
        handleCurrentDisplay,
        verificationChecks,
        verificationErrors,
        handleViewVerificationResults,
        verificationResult,
        handleCancel: handleCancelValidationTable,
        handleImport: handleStartAPICall,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
    };
};

export default function CSVInternationalRates() {
    const {
        handleUploadData,
        handleCurrentDisplay,
        verificationChecks,
        handleViewVerificationResults,
        verificationResult,
        handleCancel,
        verificationErrors,
        handleImport,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
    } = useCSVDisplayControl();

    const { handleDownloadTemplate, loading: templateLoading } =
        useCSVTemplate();

    const navigate = useNavigate();
    const { currentCompany } = useSelector((state) => state.navigationLists);

    if (!currentCompany) {
        return <Error error={nullError} />;
    }

    const currentDisplay = handleCurrentDisplay();

    return (
        <Box id="csv-international-rates">
            {currentDisplay === DOWNLOAD_CSV_TEMPLATE && (
                <CSVTemplateView
                    handleUploadData={handleUploadData}
                    downloadCSVText="Download"
                    header="CSV Upload - International Rates"
                    step1Text="Step 1 - Download the CSV template"
                    step2Text="Step 2 - Upload CSV to be imported"
                    instructionsHeader="Instructions"
                    handleDownloadTemplate={handleDownloadTemplate}
                    loading={templateLoading}
                    bgColor="white"
                    info="International Rates"
                    alertText="Manage International Rates for this rate list. You can add, edit, or delete rates using the CSV upload."
                    isPlanIdRequired={false}
                    onBack={() => navigate('/international-rates')}
                    instructionsBody={[
                        'Leave Rate ID blank to add a new rate.',
                        'Provide a Rate ID with Active=1 to edit an existing rate.',
                        'Provide a Rate ID with Active=0 to delete an existing rate.',
                        'All fields are required except Rate ID.',
                        'Cost and rate fields must be valid numbers (decimals allowed).',
                        'You are responsible for the changes you are about to make through this data import.',
                    ]}
                />
            )}
            {currentDisplay === LOADING_CSV_VALIDATION && (
                <VerifyCSVView
                    handleViewVerificationResults={
                        handleViewVerificationResults
                    }
                    verificationChecks={verificationChecks}
                />
            )}
            {currentDisplay === CSV_VALIDATION_RESULTS && (
                <VerifyCSVTableView
                    verificationResult={verificationResult}
                    handleCancel={handleCancel}
                    handleImport={handleImport}
                    handleExportInvalidData={handleExportInvalidData}
                    handleStartOver={handleStartOver}
                    passedColumns={passedColumns}
                    failedColumns={failedColumns}
                />
            )}
            {currentDisplay === API_CALLS_PROGRESS && (
                <APILoadingView
                    completionCounter={completionCounter}
                    payloads={payloads}
                    verificationResult={verificationResult}
                    errorCounter={errorCounter}
                    handleViewAPIResults={handleViewAPIResults}
                    total={total}
                />
            )}
            {currentDisplay === API_CALLS_RESULTS && (
                <APIResultsView
                    error={apiErrors}
                    succeed={apiSucceed}
                    succeedAPIColumns={succeedAPIColumns}
                    failedAPIColumns={failedAPIColumns}
                    handleExportFailedData={handleExportFailedData}
                    handleDone={handleStartOver}
                />
            )}
            {currentDisplay === CSV_DATA_NOT_FOUND && (
                <VerifyCSVView
                    handleStartOver={handleStartOver}
                    errors={verificationErrors}
                />
            )}
        </Box>
    );
}
